import { useCallback, useState } from 'react';

import { UnifiedIcon } from '@/components/UnifiedIcon';
import { Button } from '@/designSystem/atoms/Button';
import { ProgressBar } from '@/designSystem/atoms/ProgressBar';
import { ThemeToggle } from '@/designSystem/atoms/ThemeToggle';
import { RangeSlider } from '@/designSystem/inputs/RangeSlider';
import { Toggle } from '@/designSystem/inputs/Toggle';
import { SidebarNavItem, TopNavTabs } from '@/designSystem/molecules/NavTabs';
import { SelectableCard } from '@/designSystem/molecules/SelectableCard';
import { Stepper } from '@/designSystem/molecules/Stepper';
import { PreviewPanel } from '@/designSystem/organisms/PreviewPanel';

const BUILDER_STEPS = [
  { id: 'identity', label: 'Identity', state: 'complete' as const },
  { id: 'intelligence', label: 'Intelligence', state: 'active' as const },
  { id: 'knowledge', label: 'Knowledge & Tools', state: 'upcoming' as const },
  { id: 'review', label: 'Review', state: 'upcoming' as const },
];

const TOP_TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'agents', label: 'Agents', active: true },
  { id: 'logs', label: 'Logs' },
  { id: 'settings', label: 'Settings' },
];

/**
 * Living reference for the agent-builder design system (screenshot parity).
 * Route: `/design-system` (authenticated).
 */
export function DesignSystemShowcasePage() {
  const [selectedModel, setSelectedModel] = useState('gemini');
  const [temperature, setTemperature] = useState(55);
  const [fallbackEnabled, setFallbackEnabled] = useState(true);
  const [activeSidebar, setActiveSidebar] = useState('overview');

  const handleSelectModel = useCallback((id: string) => {
    setSelectedModel(id);
  }, []);

  return (
    <div data-testid="agents-design-system-showcase" className="space-y-6">
      <header data-testid="agents-design-system-showcase-header">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted">Reference UI</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-primary">Agent builder design system</h1>
            <p className="mt-2 max-w-2xl text-sm text-secondary">
              Reusable tokens and components mapped from the intelligence-selection mock. Toggle theme
              to validate light and dark surfaces.
            </p>
          </div>
          <ThemeToggle />
        </div>

        <TopNavTabs
          tabs={TOP_TABS}
          trailing={
            <>
              <button type="button" className="rounded-lg p-2 text-muted hover:bg-subtle" aria-label="Notifications">
                <UnifiedIcon name="Bell" size={20} className="h-5 w-5" />
              </button>
              <button type="button" className="rounded-lg p-2 text-muted hover:bg-subtle" aria-label="Help">
                <UnifiedIcon name="Question" size={20} className="h-5 w-5" />
              </button>
              <Button variant="primary" data-testid="agents-design-system-deploy-cta">
                Deploy now
              </Button>
            </>
          }
        />
      </header>

      <div className="grid gap-6 xl:grid-cols-[220px_minmax(0,1fr)_320px]">
        <aside data-testid="agents-design-system-showcase-sidebar" className="ds-surface p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Ops Central</p>
          <p className="text-sm font-medium text-primary">Workspace</p>
          <nav className="mt-4 space-y-1">
            <SidebarNavItem
              label="Overview"
              icon="SquaresFour"
              active={activeSidebar === 'overview'}
              onClick={() => setActiveSidebar('overview')}
              data-testid="agents-design-system-nav-overview"
            />
            <SidebarNavItem
              label="Template library"
              icon="BookOpen"
              active={activeSidebar === 'templates'}
              onClick={() => setActiveSidebar('templates')}
              data-testid="agents-design-system-nav-templates"
            />
          </nav>
          <div className="mt-8 border-t border-default pt-4">
            <SidebarNavItem label="Support" icon="Lifebuoy" data-testid="agents-design-system-nav-support" />
          </div>
        </aside>

        <section data-testid="agents-design-system-showcase-main" className="min-w-0 space-y-6">
          <Stepper steps={BUILDER_STEPS} data-testid="agents-design-system-stepper" />

          <div className="ds-surface p-6">
            <h2 className="text-lg font-semibold text-primary">Intelligence selection</h2>
            <p className="mt-1 text-sm text-muted">Choose the primary model for this agent.</p>

            <div className="mt-4 space-y-3">
              <SelectableCard
                title="Gemini 1.5 Pro"
                description="Multimodal reasoning with long context."
                meta="240ms · $1.25 / 1M tokens"
                badge="Architecture V2"
                recommended
                selected={selectedModel === 'gemini'}
                onSelect={() => handleSelectModel('gemini')}
                data-testid="agents-design-system-model-gemini"
              />
              <SelectableCard
                title="Claude 3.5 Sonnet"
                description="Strong writing and tool use."
                meta="310ms · $3.00 / 1M tokens"
                selected={selectedModel === 'claude'}
                onSelect={() => handleSelectModel('claude')}
                data-testid="agents-design-system-model-claude"
              />
              <SelectableCard
                title="GPT-4o"
                description="General-purpose flagship model."
                meta="280ms · $5.00 / 1M tokens"
                selected={selectedModel === 'gpt'}
                onSelect={() => handleSelectModel('gpt')}
                data-testid="agents-design-system-model-gpt"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="ds-surface p-5">
              <RangeSlider
                label="Creativity (temperature)"
                value={temperature}
                onChange={setTemperature}
                data-testid="agents-design-system-temperature"
              />
            </div>
            <div className="ds-surface flex items-center p-5">
              <Toggle
                checked={fallbackEnabled}
                onChange={setFallbackEnabled}
                label="Fallback model"
                description="Use a secondary model when the primary is unavailable."
                data-testid="agents-design-system-fallback-toggle"
              />
            </div>
          </div>

          <div className="ds-surface p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted">Persona alignment score</h3>
            <div className="mt-4 space-y-4">
              <ProgressBar label="Gemini 1.5 Pro" valueLabel="94% Match" value={94} />
              <ProgressBar label="Claude 3.5 Sonnet" valueLabel="89% Match" value={89} muted />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="secondary" data-testid="agents-design-system-back">
              <UnifiedIcon name="ArrowLeft" size={16} className="h-4 w-4" />
              Back
            </Button>
            <Button variant="glow" data-testid="agents-design-system-next">
              Next: Knowledge &amp; Tools
            </Button>
          </div>
        </section>

        <PreviewPanel
          subtitle="Updates as you configure the agent."
          footer={
            <>
              <div className="flex justify-between text-secondary">
                <span>Model</span>
                <span className="font-medium text-primary">Gemini 1.5 Pro</span>
              </div>
              <div className="flex justify-between text-secondary">
                <span>Temperature</span>
                <span className="font-medium text-primary">{temperature}%</span>
              </div>
              <div className="flex justify-between text-secondary">
                <span>Fallback</span>
                <span className="font-medium text-primary">{fallbackEnabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            </>
          }
        />
      </div>
    </div>
  );
}
